import { useState } from "react";
import { Link } from "react-router-dom";

import { acceptInvite, declineInvite, markNotificationRead } from "@/lib/api";
import type { Notification, NotificationKind } from "@/lib/types";

// Mongo round-trips datetimes as naive (tzinfo dropped) — same fix as the discussions UI.
function parseUtc(dateStr: string): Date {
  return new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`);
}

export function notifTimeAgo(dateStr: string): string {
  const diff = Date.now() - parseUtc(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// "Today" / "Yesterday" / "Jul 12" — the date-group headers on the full notifications page.
export function dayLabel(dateStr: string): string {
  const d = parseUtc(dateStr);
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function NotificationIcon({ kind, className = "h-4 w-4" }: { kind: NotificationKind; className?: string }) {
  if (kind === "team_invite") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (kind === "follow") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" />
      </svg>
    );
  }
  if (kind === "version_release") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  if (kind === "suggestion_received") {
    // An inbound suggestion on a skill you own — a pencil/edit glyph.
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    );
  }
  if (kind === "suggestion_merged") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" />
      </svg>
    );
  }
  if (kind === "suggestion_rejected") {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    );
  }
  if (kind === "skill_forked") {
    // Git fork icon — two branches splitting from a shared node.
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="2" /><circle cx="18" cy="6" r="2" /><circle cx="12" cy="19" r="2" />
        <path d="M6 8v2a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4V8" /><line x1="12" y1="13" x2="12" y2="17" />
      </svg>
    );
  }
  // thread_reply (default) — speech bubble, matches the Discussions "question" icon.
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function NotificationRow({
  n,
  dense = false,
  onOpen,
  token,
  onResolved,
}: {
  n: Notification;
  dense?: boolean;
  onOpen?: () => void;
  token?: string | null;
  onResolved?: () => void; // called after a team invite is accepted/declined (to refresh unread count)
}) {
  // Team invites are actionable, not a plain navigating row.
  if (n.kind === "team_invite") {
    return <TeamInviteRow n={n} dense={dense} token={token} onResolved={onResolved} />;
  }
  const isFollow = n.kind === "follow" || n.kind === "skill_forked";
  return (
    <Link
      to={n.link}
      onClick={onOpen}
      className={`flex items-start gap-3 outline-none transition hover:bg-white/[0.03] focus-visible:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 ${dense ? "px-5 py-3" : "py-4"}`}
    >
      <span
        className={`flex shrink-0 items-center justify-center rounded-md border ${dense ? "h-8 w-8" : "h-11 w-11"} ${
          isFollow
            ? "border-emerald-500/25 bg-emerald-500/[0.1] text-emerald-600 dark:text-emerald-400"
            : "border-primary/20 bg-primary/[0.08] text-primary"
        }`}
      >
        <NotificationIcon kind={n.kind} className={dense ? "h-4 w-4" : "h-[18px] w-[18px]"} />
      </span>
      <span className={`min-w-0 flex-1 ${dense ? "text-[13px]" : "text-sm"} ${n.read ? "text-text/60" : "text-text/90"}`}>
        {n.text}
      </span>
      {(!n.read || !dense) && (
        <span className={`flex shrink-0 items-center gap-2 ${dense ? "pl-1" : "pl-2"}`}>
          {!n.read && <span className={`h-[7px] w-[7px] rounded-full ${isFollow ? "bg-emerald-500" : "bg-primary"}`} />}
          {!dense && <span className="font-mono text-[11px] text-text/35">{notifTimeAgo(n.created_at)}</span>}
        </span>
      )}
    </Link>
  );
}

// Team-invite notification — actionable inline (Accept / Decline) using the invite id carried in meta.
function TeamInviteRow({ n, dense, token, onResolved }: {
  n: Notification;
  dense: boolean;
  token?: string | null;
  onResolved?: () => void;
}) {
  const [state, setState] = useState<"idle" | "busy" | "accepted" | "declined" | "gone">("idle");
  const inviteId = n.meta?.invite_id;

  async function act(kind: "accept" | "decline") {
    if (!token || !inviteId || state === "busy") return;
    setState("busy");
    try {
      if (kind === "accept") await acceptInvite(inviteId, token);
      else await declineInvite(inviteId, token);
      setState(kind === "accept" ? "accepted" : "declined");
      markNotificationRead(n.id, token).catch(() => {});
      onResolved?.();
    } catch {
      setState("gone"); // invite already handled or expired
    }
  }

  const actionable = state === "idle" && !!inviteId && !!token;
  const iconTile = (
    <span className={`flex shrink-0 items-center justify-center rounded-md border border-connected/25 bg-connected/10 text-connected ${dense ? "h-8 w-8" : "h-11 w-11"}`}>
      <NotificationIcon kind="team_invite" className={dense ? "h-4 w-4" : "h-[18px] w-[18px]"} />
    </span>
  );
  const right = actionable ? (
    <div className="flex shrink-0 gap-2">
      <button onClick={() => act("accept")}
        className="rounded-lg bg-primary px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-hover">Accept</button>
      <button onClick={() => act("decline")}
        className="rounded-lg border border-[var(--border)] px-3.5 py-1.5 text-xs text-text/70 transition hover:border-red-400 hover:text-red-500">Decline</button>
    </div>
  ) : state === "busy" ? <span className="shrink-0 text-xs text-text/45">Working…</span>
    : state === "accepted" ? <span className="shrink-0 text-xs font-medium text-emerald-600 dark:text-emerald-400">Joined {n.meta?.team_name ?? "the team"}</span>
    : state === "declined" ? <span className="shrink-0 text-xs text-text/45">Declined</span>
    : state === "gone" ? <span className="shrink-0 text-xs text-text/45">No longer available</span>
    : null;
  const textCls = `${dense ? "text-[13px]" : "text-sm"} ${n.read ? "text-text/60" : "text-text/90"}`;

  // Dropdown (dense) is narrow → stack the actions under the text; the full page keeps them inline.
  if (dense) {
    return (
      <div className="flex items-start gap-3 px-5 py-3">
        {iconTile}
        <div className="min-w-0 flex-1">
          <p className={textCls}>{n.text}</p>
          {right && <div className="mt-2">{right}</div>}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 py-4">
      {iconTile}
      <p className={`min-w-0 flex-1 ${textCls}`}>{n.text}</p>
      {right}
      <span className="shrink-0 pl-1 font-mono text-[11px] text-text/35">{notifTimeAgo(n.created_at)}</span>
    </div>
  );
}
